import type { AuthSessionState } from "./AuthSessionBoundary";

export const PRODUCT_SESSION_STORAGE_KEY = "artist.product-session.v1";
export const PRODUCT_SCOPE_WATERMARK_STORAGE_KEY = "artist.scope-watermark.v1";
export const LOCAL_EVIDENCE_OWNER = "00000000-0000-4000-8000-000000000001";
export const LOCAL_EVIDENCE_SCOPE_EPOCH = 7;

export interface ProductScopeBinding {
	readonly ownerUserId: string;
	readonly scopeEpoch: number;
	readonly attestation: "local-evidence-v1" | "provider-session-v1";
}

export function nextProviderScopeEpoch(rawWatermark: string | null): number {
	if (rawWatermark === null) return 1;
	const watermark = Number(rawWatermark);
	if (
		!Number.isSafeInteger(watermark) ||
		watermark < 1 ||
		watermark >= Number.MAX_SAFE_INTEGER
	) {
		throw new TypeError("scope watermark is malformed or exhausted");
	}
	return watermark + 1;
}

export function createProviderProductSession(
	session: AuthoritativeProviderSession,
	scopeEpoch: number,
): PersistedProductSession {
	if (!UUID_PATTERN.test(session.userId)) {
		throw new TypeError("provider user id must be a UUID");
	}
	if (!Number.isSafeInteger(scopeEpoch) || scopeEpoch < 1) {
		throw new TypeError("scope epoch must be a positive safe integer");
	}
	if (
		session.email.trim().length === 0 ||
		!Number.isSafeInteger(session.expiresAtEpochSeconds)
	) {
		throw new TypeError("provider session metadata is malformed");
	}
	return {
		schemaVersion: "1.0.0",
		mode: "provider",
		email: session.email,
		ownerUserId: session.userId,
		scopeEpoch,
		expiresAtEpochSeconds: session.expiresAtEpochSeconds,
	};
}

export interface AuthoritativeProviderSession {
	readonly userId: string;
	readonly email: string;
	readonly expiresAtEpochSeconds: number;
}

export interface PersistedProductSession {
	readonly schemaVersion: "1.0.0";
	readonly mode: "local-evidence" | "provider";
	readonly email: string;
	readonly ownerUserId: string;
	readonly scopeEpoch: number;
	readonly expiresAtEpochSeconds: number;
}

export const AWAITING_AUTH_SESSION_STATE: AuthSessionState = Object.freeze({
	bootstrapPhase: "awaiting-initial",
	userId: null,
	expiresAtEpochSeconds: null,
	userHintId: null,
	postLoginPreparedForUserId: null,
});

const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;

export function createEvidenceProductSession(
	email: string,
	nowEpochSeconds: number,
	scopeEpoch = LOCAL_EVIDENCE_SCOPE_EPOCH,
): PersistedProductSession {
	if (
		!Number.isSafeInteger(scopeEpoch) ||
		scopeEpoch < LOCAL_EVIDENCE_SCOPE_EPOCH
	) {
		throw new TypeError("scope epoch must be a safe monotonic evidence epoch");
	}
	return {
		schemaVersion: "1.0.0",
		mode: "local-evidence",
		email,
		ownerUserId: LOCAL_EVIDENCE_OWNER,
		scopeEpoch,
		expiresAtEpochSeconds: nowEpochSeconds + 86_400,
	};
}

export function parsePersistedProductSession(
	rawValue: string | null,
): PersistedProductSession | null {
	if (rawValue === null) return null;
	try {
		const value: unknown = JSON.parse(rawValue);
		if (!isRecord(value)) return null;
		const keys = Object.keys(value).toSorted();
		if (
			keys.join("|") !==
			"email|expiresAtEpochSeconds|mode|ownerUserId|schemaVersion|scopeEpoch"
		)
			return null;
		if (
			value.schemaVersion !== "1.0.0" ||
			(value.mode !== "local-evidence" && value.mode !== "provider") ||
			typeof value.email !== "string" ||
			value.email.length === 0 ||
			typeof value.ownerUserId !== "string" ||
			!UUID_PATTERN.test(value.ownerUserId) ||
			!Number.isSafeInteger(value.scopeEpoch) ||
			(value.scopeEpoch as number) < LOCAL_EVIDENCE_SCOPE_EPOCH ||
			!Number.isSafeInteger(value.expiresAtEpochSeconds)
		) {
			return null;
		}
		if (value.mode === "local-evidence" && value.ownerUserId !== LOCAL_EVIDENCE_OWNER) {
			return null;
		}
		return value as unknown as PersistedProductSession;
	} catch {
		return null;
	}
}

export function nextEvidenceScopeEpoch(rawWatermark: string | null): number {
	if (rawWatermark === null) return LOCAL_EVIDENCE_SCOPE_EPOCH;
	const watermark = Number(rawWatermark);
	if (
		!Number.isSafeInteger(watermark) ||
		watermark < LOCAL_EVIDENCE_SCOPE_EPOCH ||
		watermark >= Number.MAX_SAFE_INTEGER
	) {
		throw new TypeError("scope watermark is malformed or exhausted");
	}
	return watermark + 1;
}

export function productScopeBinding(
	session: PersistedProductSession,
): ProductScopeBinding {
	return Object.freeze({
		ownerUserId: session.ownerUserId,
		scopeEpoch: session.scopeEpoch,
		attestation:
			session.mode === "provider"
				? "provider-session-v1"
				: "local-evidence-v1",
	});
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
